/**
 * Who an invoice is from. An invoice names exactly one sender: a payee (a
 * vendor — Tibber, Telia, …) through `payeeId`, or one of the user's own
 * accounts (a card statement) through `sourceId`. This is the one place that
 * decides which field wins, so the rest of the UI never checks the two ids
 * itself.
 */
import type {
  InvoiceEntity,
  PayeeEntity,
  PayeeId,
  SourceEntity,
  SourceId,
} from "../api/types";

/** Doubles as the logo kind: `/api/logo/{kind}/{id}`. */
export type SenderKind = "payee" | "source";

/** What a sender of each kind is called in the UI (picker labels, rows). */
export const SENDER_LABEL: Record<SenderKind, string> = {
  payee: "Payee",
  source: "Source",
};

/** The sender an invoice names, before looking it up in a registry. */
export type InvoiceSenderRef =
  | { kind: "payee"; id: PayeeId }
  | { kind: "source"; id: SourceId };

/** The sender resolved against a registry; `entity` is undefined when the id
 * names nothing there. Resolve against `useRegistry().withHidden`, so that is
 * only a deleted sender — a hidden one is still named. */
export type InvoiceSender =
  | { kind: "payee"; id: PayeeId; entity: PayeeEntity | undefined }
  | { kind: "source"; id: SourceId; entity: SourceEntity | undefined };

/** The slice of the registry a sender resolves against (`useRegistry().withHidden`
 * fits). */
export type SenderRegistry = {
  payees: readonly PayeeEntity[];
  sources: readonly SourceEntity[];
};

/** Stable across kinds, so a payee and a source never collide in one list. */
export type SenderKey = `${SenderKind}:${string}`;

type SenderFields = Pick<InvoiceEntity, "payeeId" | "sourceId">;

/**
 * The sender an invoice names, or null if it names neither (the server refuses
 * that, so it only shows up for a malformed row). A payee wins should both
 * ever be set.
 */
export const invoiceSenderRef = (
  invoice: SenderFields,
): InvoiceSenderRef | null => {
  if (invoice.payeeId) return { kind: "payee", id: invoice.payeeId };
  if (invoice.sourceId) return { kind: "source", id: invoice.sourceId };
  return null;
};

export const invoiceSender = (
  invoice: SenderFields,
  registry: SenderRegistry,
): InvoiceSender | null => {
  const ref = invoiceSenderRef(invoice);
  if (!ref) return null;
  return ref.kind === "payee"
    ? { ...ref, entity: registry.payees.find((p) => p.id === ref.id) }
    : { ...ref, entity: registry.sources.find((s) => s.id === ref.id) };
};

export const senderKey = (ref: InvoiceSenderRef): SenderKey =>
  `${ref.kind}:${ref.id}`;

/** The key of the sender an invoice names, for filtering without a registry. */
export const invoiceSenderKey = (invoice: SenderFields): SenderKey | null => {
  const ref = invoiceSenderRef(invoice);
  return ref ? senderKey(ref) : null;
};

const UNKNOWN: Record<SenderKind, string> = {
  payee: "Unknown payee",
  source: "Unknown source",
};

/** Display name, with a kind-specific placeholder when the entity is missing. */
export const senderName = (sender: InvoiceSender | null): string =>
  sender ? (sender.entity?.name ?? UNKNOWN[sender.kind]) : "Unknown sender";

/** Whether an import can be linked to this invoice: only an account's can. */
export const isAccountInvoice = (invoice: SenderFields): boolean =>
  invoiceSenderRef(invoice)?.kind === "source";

/**
 * The invoice form's sender while it is being edited: which kind is chosen,
 * plus the last id picked for each kind, so flipping the toggle back and forth
 * doesn't lose a pick. Only the chosen kind's id is ever saved.
 */
export type SenderDraft = {
  kind: SenderKind;
  payeeId: PayeeId | null;
  sourceId: SourceId | null;
};

/** A new invoice starts on Payee (most invoices are bills from a vendor); an
 * existing one opens on whichever sender it names. */
export const senderDraftFor = (
  invoice: SenderFields | null | undefined,
): SenderDraft => {
  const ref = invoice ? invoiceSenderRef(invoice) : null;
  return {
    kind: ref?.kind ?? "payee",
    payeeId: ref?.kind === "payee" ? ref.id : null,
    sourceId: ref?.kind === "source" ? ref.id : null,
  };
};

/** The id picked for the chosen kind, or null when none is picked yet. */
export const draftSenderId = (draft: SenderDraft): string | null =>
  draft.kind === "payee" ? draft.payeeId : draft.sourceId;

/** The wire fields for a draft: the chosen kind's id, the other one null. */
export const draftSenderFields = (
  draft: SenderDraft,
): { payeeId: PayeeId | null; sourceId: SourceId | null } =>
  draft.kind === "payee"
    ? { payeeId: draft.payeeId, sourceId: null }
    : { payeeId: null, sourceId: draft.sourceId };

/** Set one kind's id and switch the draft to that kind. */
export const draftWithSender = (
  draft: SenderDraft,
  ref: InvoiceSenderRef,
): SenderDraft =>
  ref.kind === "payee"
    ? { ...draft, kind: "payee", payeeId: ref.id }
    : { ...draft, kind: "source", sourceId: ref.id };
